import {
  ProfessionalAvailability,
  ProfessionalAvailabilityInputModel,
  AvailableDate,
} from '../types/AvailabilityModels';
import {
  addDays,
  isWithinInterval,
  startOfDay,
  endOfDay,
  parse,
} from 'date-fns';
import { api } from './api';
import { htmlDecode } from '../utils/htmlDecode';

const decodeAvailabilityTopics = (
  availability: ProfessionalAvailability
): ProfessionalAvailability => {
  return {
    ...availability,
    availableDates: availability.availableDates.map((date) => ({
      ...date,
      availableTimes: date.availableTimes.map((time) => ({
        ...time,
        topics: (time.topics ?? []).map((topic) => ({
          ...topic,
          title: htmlDecode(topic.title),
          description: topic.description
            ? htmlDecode(topic.description)
            : undefined,
        })),
      })),
    })),
  };
};

export const getProfessionalAvailability = async (
  professionalId: string,
  token: string
): Promise<ProfessionalAvailability | null> => {
  try {
    const response = await api.get<ProfessionalAvailability>(
      `/v1/availability/professional?id=${professionalId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return decodeAvailabilityTopics(response.data);
  } catch (error) {
    console.error('Error fetching professional availability:', error);
    return null;
  }
};

export const postProfessionalAvailability = async (
  professionalAvailability: ProfessionalAvailabilityInputModel,
  token: string
): Promise<void> => {
  try {
    await api.post(`/v1/availability/add`, professionalAvailability, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    console.error('Error posting professional availability:', error);
    throw error;
  }
};

export const getProfessionalAvailabilityForNextTwoWeeks = async (
  userId: string,
  token: string
): Promise<AvailableDate[]> => {
  try {
    const results = await getProfessionalAvailability(userId, token);
    if (!results) {
      return [];
    }

    const today = startOfDay(new Date());
    const endDate = endOfDay(addDays(today, 14));

    const availableDatesInNextTwoWeeks = results.availableDates.filter(
      (availableDate: AvailableDate) => {
        const date = startOfDay(
          parse(availableDate.date, 'MM/dd/yyyy', new Date())
        );

        return isWithinInterval(date, { start: today, end: endDate });
      }
    );

    return availableDatesInNextTwoWeeks;
  } catch (error) {
    console.error(
      'Error fetching professional availability for next two weeks:',
      error
    );
    throw error;
  }
};

export const deleteAvailability = async (
  availabilityId: number,
  token: string
): Promise<void> => {
  try {
    const model = { availabilityId };
    await api.delete(`/v1/availability`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: model,
    });
  } catch (error) {
    console.error('Error deleting availability:', error);
    throw error;
  }
};

export const bulkDeleteAvailability = async (
  availabilityIds: number[],
  token: string
): Promise<void> => {
  try {
    await api.delete(`/v1/availability/bulk`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: { availabilityIds },
    });
  } catch (error) {
    console.error('Error bulk deleting availability:', error);
    throw error;
  }
};
